/**
 * E-Commerce System Core
 * Main system class that coordinates all modules with dependency injection
 */

import { getStorage, setStorage, clearStorage } from './storage.js';
import { getElement, getAllElements, escapeHtml, formatPrice, debounce, dispatchCartUpdatedEvent } from './utils.js';
import { ShoppingCart } from '../modules/cart.js';
import { ProductManager } from '../modules/products.js';
import { FilterManager } from '../modules/filters.js';
import { SearchDropdown } from '../modules/search.js';
import { SearchPage } from '../modules/search-page.js';
import { ProductVariantManager } from '../modules/variants.js';
import { CategoryVariantManager } from '../modules/category-variants.js';
import { CartHeaderButton } from '../modules/cart-button.js';
import { InlineCart } from '../modules/inline-cart.js';
import { ShippingOptionsManager } from '../modules/shipping-options.js';
import { DiscountManager } from '../modules/discount.js';
import { CheckoutManager } from '../checkout/manager.js';
import { CustomerFormHandler } from '../checkout/customer-form.js';

export class ECommerceSystem {
    constructor(config = {}) {
        this.config = {
            storageKey: 'cartItems',
            searchApi: config.searchApi || null, // Måste sättas via config
            searchMinChars: config.searchMinChars || 2,
            searchDebounceMs: config.searchDebounceMs || 300,
            searchMaxResults: config.searchMaxResults || 8,
            showCategories: config.showCategories !== false,
            debug: config.debug || false,
            ...config
        };

        // Cache för DOM-element
        this.elements = new Map();
        
        // Initialisera moduler med säkerhetskontroller
        this.initializeModules();
        
        // Skapa modules-objekt för bakåtkompatibilitet med setupModuleIntegration()
        this.modules = {
            cart: this.cart,
            products: this.products,
            filters: this.filters,
            search: this.search,
            searchPage: this.searchPage,
            checkout: this.checkout,
            cartButton: this.cartButton,
            inlineCart: this.inlineCart,
            variants: this.variants,
            categoryVariants: this.categoryVariants,
            shippingOptions: this.shippingOptions,
            discount: this.discount
        };
        
        this.init();
    }

    initializeModules() {
        try {
            this.cart = new ShoppingCart(this);
        } catch (error) {
            console.error('Failed to initialize ShoppingCart:', error);
            this.cart = null;
        }
        
        try {
            this.products = new ProductManager(this);
        } catch (error) {
            console.error('Failed to initialize ProductManager:', error);
            this.products = null;
        }
        
        try {
            this.filters = new FilterManager(this);
        } catch (error) {
            console.error('Failed to initialize FilterManager:', error);
            this.filters = null;
        }
        
        try {
            this.search = new SearchDropdown(this);
        } catch (error) {
            console.error('Failed to initialize SearchDropdown:', error);
            this.search = null;
        }
        
        try {
            this.searchPage = new SearchPage(this);
        } catch (error) {
            console.error('Failed to initialize SearchPage:', error);
            this.searchPage = null;
        }
        
        try {
            this.checkout = new CheckoutManager(this);
        } catch (error) {
            console.error('Failed to initialize CheckoutManager:', error);
            this.checkout = null;
        }
        
        try {
            this.cartButton = new CartHeaderButton(this);
        } catch (error) {
            console.error('Failed to initialize CartHeaderButton:', error);
            this.cartButton = null;
        }

        try {
            this.inlineCart = new InlineCart(this);
        } catch (error) {
            console.error('Failed to initialize InlineCart:', error);
            this.inlineCart = null;
        }
        
        try {
            this.variants = new ProductVariantManager(this);
        } catch (error) {
            console.error('Failed to initialize ProductVariantManager:', error);
            this.variants = null;
        }

        try {
            this.categoryVariants = new CategoryVariantManager(this);
        } catch (error) {
            console.error('Failed to initialize CategoryVariantManager:', error);
            this.categoryVariants = null;
        }
        
        try {
            this.shippingOptions = new ShippingOptionsManager(this);
        } catch (error) {
            console.error('Failed to initialize ShippingOptionsManager:', error);
            this.shippingOptions = null;
        }
        
        // Only initialize DiscountManager on checkout pages
        if (this.isCheckoutPage()) {
            try {
                this.discount = new DiscountManager(this);
            } catch (error) {
                console.error('Failed to initialize DiscountManager:', error);
                this.discount = null;
            }
        } else {
            this.discount = null;
        }
    }

    init() {
        // Vänta på DOM
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setupEventListeners());
        } else {
            this.setupEventListeners();
        }
    }

    setupEventListeners() {
        // Cross-tab sync: localStorage writes in OTHER tabs fire 'storage' here.
        // Re-dispatch as cartUpdated so the badge, an open inline cart panel and
        // product buttons refresh through their existing listeners.
        window.addEventListener('storage', (event) => {
            if (event.key === this.config.storageKey) {
                dispatchCartUpdatedEvent(this.getStorage());
            }
        });

        // Säkerställ att alla moduler finns innan vi anropar init
        if (this.cart && typeof this.cart.init === 'function') {
            this.cart.init();
        }
        if (this.products && typeof this.products.init === 'function') {
            this.products.init();
        }
        if (this.filters && typeof this.filters.init === 'function') {
            this.filters.init();
        }
        if (this.checkout && typeof this.checkout.init === 'function') {
            this.checkout.init();
        }
        if (this.cartButton && typeof this.cartButton.init === 'function') {
            this.cartButton.init();
        }
        if (this.inlineCart && typeof this.inlineCart.init === 'function') {
            this.inlineCart.init(); // Slide-in cart panel (all pages)
        }
        if (this.search && typeof this.search.init === 'function') {
            this.search.init(); // Automatisk initialisering av sökfält
        }
        if (this.searchPage && typeof this.searchPage.init === 'function') {
            this.searchPage.init(); // Automatisk initialisering av söksida
        }
        // ORDER MATTERS between variants.init() and categoryVariants.init():
        // on pages without window.productVariantData (e.g. CMS pages with
        // ProductParts), variants.init() falls through to setupStandardInterface()
        // which hides the FIRST .js-variant-selectors in the document.
        // categoryVariants.init() below re-shows the selectors per card.
        // Do not reorder these two calls.
        if (this.variants && typeof this.variants.init === 'function') {
            this.variants.init(); // Automatisk initialisering av variant-hantering
            
            // Expose ProductVariantManager globally for other modules (e.g., GiftCardFormManager)
            window.productVariantManager = this.variants;
        }
        if (this.categoryVariants && typeof this.categoryVariants.init === 'function') {
            this.categoryVariants.init(); // Scoped variant handling per product card (category pages, CMS ProductParts)
        }
        if (this.shippingOptions && typeof this.shippingOptions.init === 'function') {
            this.shippingOptions.init(); // Automatisk initialisering av leveransalternativ
        }
        if (this.discount && typeof this.discount.init === 'function') {
            this.discount.init(); // Automatisk initialisering av rabatt-hantering
        }
    }

    // Gemensamma hjälpfunktioner (delegerar till utils)
    getElement(selector) {
        return getElement(selector, this.elements);
    }

    getAllElements(selector) {
        return getAllElements(selector);
    }

    // Storage-hantering (delegerar till storage)
    getStorage() {
        return getStorage(this.config.storageKey);
    }

    setStorage(items) {
        return setStorage(this.config.storageKey, items);
    }

    clearStorage() {
        return clearStorage(this.config.storageKey);
    }

    // Hjälpfunktioner (delegerar till utils)
    escapeHtml(text) {
        return escapeHtml(text);
    }

    formatPrice(price) {
        return formatPrice(price);
    }

    debounce(func, wait) {
        return debounce(func, wait);
    }

    // Helper method to check if current page is a checkout page
    isCheckoutPage() {
        // Check for checkout-specific elements
        const checkoutElements = [
            '#discount-section',
            '#customer-form', 
            '#klarna',
            '#bambora-checkout',
            '#checkout-container',
            '.checkout-page'
        ];
        
        return checkoutElements.some(selector => document.querySelector(selector) !== null);
    }

    // Public API methods
    
    /**
     * Get current cart items
     * @returns {Array} Cart items
     */
    getCartItems() {
        return this.getStorage();
    }

    /**
     * Get cart total count
     * @returns {number} Total number of items in cart
     */
    getCartCount() {
        return this.getStorage().reduce((sum, item) => sum + item.OrderCount, 0);
    }

    /**
     * Get cart total price
     * @returns {number} Total price of items in cart
     */
    getCartTotal() {
        return this.getStorage().reduce((sum, item) => {
            return sum + (parseFloat(item.ProductPrice) * item.OrderCount);
        }, 0);
    }

    /**
     * Add product to cart
     * @param {Object} product - Product data
     * @returns {boolean} Success status
     */
    addToCart(product) {
        if (this.products) {
            const items = this.getStorage();
            items.push(product);
            return this.setStorage(items);
        }
        return false;
    }

    /**
     * Remove product from cart
     * @param {string} productId - Product ID to remove
     * @returns {boolean} Success status
     */
    removeFromCart(productId) {
        if (this.cart) {
            this.cart.removeProduct(productId);
            return true;
        }
        return false;
    }

    /**
     * Empty the cart
     * @returns {boolean} Success status
     */
    emptyCart() {
        if (this.cart) {
            this.cart.emptyCart();
            return true;
        }
        return this.clearStorage();
    }

    /**
     * Switch payment provider
     * @param {string} provider - Provider name (klarna, bambora, none)
     * @returns {boolean} Success status
     */
    switchPaymentProvider(provider) {
        if (this.checkout) {
            return this.checkout.setProvider(provider);
        }
        return false;
    }

    /**
     * Get current payment provider
     * @returns {string} Current provider name
     */
    getCurrentPaymentProvider() {
        if (this.checkout) {
            return this.checkout.getCurrentProviderType();
        }
        return 'unknown';
    }

    /**
     * Create search dropdown in container
     * @param {string} containerId - Container ID
     * @param {Object} options - Search options
     * @returns {Object|null} Search instance or null
     */
    createSearchDropdown(containerId, options = {}) {
        if (this.search) {
            return this.search.create(containerId, options);
        }
        return null;
    }

    /**
     * Get current selected variant (if any)
     * @returns {Object|null} Current variant or null
     */
    getCurrentVariant() {
        if (this.variants && this.variants.isInitialized()) {
            return this.variants.getCurrentVariant();
        }
        return null;
    }

    /**
     * Refresh cart button display
     */
    refreshCartButton() {
        if (this.cartButton) {
            this.cartButton.refresh();
        }
    }

    /**
     * Open the inline cart panel
     * @param {Element} triggerElement - Element that triggered the open (for focus restore)
     */
    openCart(triggerElement = null) {
        if (this.inlineCart) {
            this.inlineCart.open(triggerElement);
        }
    }

    /**
     * Close the inline cart panel
     */
    closeCart() {
        if (this.inlineCart) {
            this.inlineCart.close();
        }
    }

    /**
     * Get system configuration
     * @returns {Object} Current configuration
     */
    getConfig() {
        return { ...this.config };
    }

    /**
     * Update system configuration
     * @param {Object} newConfig - New configuration values
     */
    updateConfig(newConfig) {
        this.config = { ...this.config, ...newConfig };
    }

    /**
     * Get system status and module information
     * @returns {Object} System status
     */
    getSystemStatus() {
        return {
            initialized: true,
            modules: {
                cart: !!this.cart,
                products: !!this.products,
                filters: !!this.filters,
                search: !!this.search,
                searchPage: !!this.searchPage && this.searchPage.isInitialized(),
                checkout: !!this.checkout,
                cartButton: !!this.cartButton,
                inlineCart: !!this.inlineCart,
                variants: !!this.variants && this.variants.isInitialized(),
                categoryVariants: !!this.categoryVariants && this.categoryVariants.isInitialized(),
                shippingOptions: !!this.shippingOptions,
                discount: !!this.discount
            },
            config: this.getConfig(),
            cartCount: this.getCartCount(),
            cartTotal: this.getCartTotal()
        };
    }
}
